import React, { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Stats from "@/components/Stats";
import USPs from "@/components/USPs";
import VideoTour from "@/components/VideoTour";
import Courses from "@/components/Courses";
import CareerGuidance from "@/components/CareerGuidance";
import Partnership from "@/components/Partnership";
import Placements from "@/components/Placements";
import FeesEMI from "@/components/FeesEMI";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import ConsultationDialog, { useExitIntent } from "@/components/ConsultationDialog";

import AdminLogin from "@/pages/AdminLogin";
import AdminLeads from "@/pages/AdminLeads";
import AdminToppers from "@/pages/AdminToppers";
import AdminContent from "@/pages/AdminContent";
import AdminFaculty from "@/pages/AdminFaculty";
import AdminStudents from "@/pages/AdminStudents";
import CoursePage from "@/pages/CoursePage";
import Toppers from "@/pages/Toppers";
import FacultyPage from "@/pages/FacultyPage";
import StudentLogin from "@/pages/StudentLogin";
import StudentDashboard from "@/pages/StudentDashboard";
import StudentExam from "@/pages/StudentExam";
import { AuthProvider } from "@/lib/auth";
import { StudentAuthProvider } from "@/lib/studentAuth";
import { ContentProvider } from "@/lib/content";

function HomePage() {
  const [open, setOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);

  const onConsult = useCallback(() => setOpen(true), []);

  useExitIntent(() => {
    if (!open && !exitOpen) setExitOpen(true);
  }, true);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("in-view"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="App font-inter text-slate-800">
      <Header onConsult={onConsult} />
      <main>
        <Hero onConsult={onConsult} />
        <Marquee />
        <Stats />
        <USPs />
        <VideoTour />
        <Courses onConsult={onConsult} />
        <CareerGuidance onConsult={onConsult} />
        <Partnership />
        <Placements />
        <FeesEMI onConsult={onConsult} />
        <ContactForm />
      </main>
      <Footer />
      <FloatingButtons />
      <ConsultationDialog open={open} onOpenChange={setOpen} source="primary-cta" />
      <ConsultationDialog open={exitOpen} onOpenChange={setExitOpen} source="exit-intent" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StudentAuthProvider>
          <ContentProvider>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/courses/:slug" element={<CoursePage />} />
              <Route path="/toppers" element={<Toppers />} />
              <Route path="/faculty" element={<FacultyPage />} />
              <Route path="/student/login" element={<StudentLogin />} />
              <Route path="/student/dashboard" element={<StudentDashboard />} />
              <Route path="/student/exam/:examId" element={<StudentExam />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/toppers" element={<AdminToppers />} />
              <Route path="/admin/faculty" element={<AdminFaculty />} />
              <Route path="/admin/students" element={<AdminStudents />} />
              <Route path="/admin/content" element={<AdminContent />} />
            </Routes>
            <Toaster position="top-right" richColors closeButton />
          </ContentProvider>
        </StudentAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
